'use client';

import { Paperclip, Trash2, X } from 'lucide-react';
import * as React from 'react';

import { Dialog, DialogContent, DialogTitle } from '../../molecules/dialog/dialog';

export type AdminNotificationTone = 'pinned' | 'unread' | 'read' | 'sent';

const ROW_STYLE: Record<
	AdminNotificationTone,
	{ bg: string; border: string; iconBg: string; dateColor: string }
> = {
	pinned: {
		bg: 'rgba(239,68,68,0.12)',
		border: '1px solid rgba(239,68,68,0.4)',
		iconBg: 'rgba(239,68,68,0.25)',
		dateColor: 'var(--color-danger)',
	},
	unread: {
		bg: 'rgba(245,158,11,0.13)',
		border: '1px solid rgba(245,158,11,0.4)',
		iconBg: 'rgba(245,158,11,0.3)',
		dateColor: 'var(--color-warn)',
	},
	read: {
		bg: 'color-mix(in srgb, var(--color-accent) 5%, transparent)',
		border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)',
		iconBg: 'color-mix(in srgb, var(--color-accent) 18%, transparent)',
		dateColor: 'var(--color-accent)',
	},
	sent: {
		bg: 'color-mix(in srgb, var(--color-accent-purple) 5%, transparent)',
		border: '1px solid color-mix(in srgb, var(--color-accent-purple) 25%, transparent)',
		iconBg: 'color-mix(in srgb, var(--color-accent-purple) 20%, transparent)',
		dateColor: 'var(--color-accent-purple)',
	},
};

export interface AdminNotificationRowProps {
	/** Colore/priorità della riga (fissata in alto, non letta, letta). */
	tone: AdminNotificationTone;
	/** Emoji/icona mostrata nel cerchio a sinistra. */
	emoji: React.ReactNode;
	/** Titolo della notifica. */
	title: React.ReactNode;
	/** Testo della notifica. */
	message?: React.ReactNode | null;
	/** Testo secondario (data/mittente), colorato in base al tono. */
	metaLabel: React.ReactNode;
	/** Chiamata all'apertura della notifica. */
	onOpen: () => void;
	/** Se presente, mostra il cestino a destra e chiama questa funzione al click. */
	onDelete?: () => void;
}

/** Riga di notifica in entrata/di sistema per il centro notifiche admin. */
const AdminNotificationRow = ({
	tone,
	emoji,
	title,
	message,
	metaLabel,
	onOpen,
	onDelete,
}: AdminNotificationRowProps) => {
	const s = ROW_STYLE[tone];
	return (
		<div style={{ background: s.bg, border: s.border, borderRadius: 8, padding: 10 }}>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
				<div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flex: 1 }}>
					{/* Circle icon */}
					<div
						style={{
							width: 26,
							height: 26,
							borderRadius: '50%',
							background: s.iconBg,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
							fontSize: 14,
							lineHeight: 1,
						}}
					>
						{emoji}
					</div>

					{/* Title + desc + meta */}
					<button
						type="button"
						onClick={onOpen}
						style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							padding: 0,
							textAlign: 'left',
							flex: 1,
						}}
					>
						<div
							style={{
								fontSize: 13,
								fontWeight: 700,
								display: 'flex',
								alignItems: 'center',
								gap: 5,
							}}
						>
							{title}
							{tone === 'unread' && (
								<span
									style={{
										display: 'inline-block',
										width: 6,
										height: 6,
										borderRadius: '50%',
										background: 'var(--color-warn)',
										flexShrink: 0,
									}}
								/>
							)}
						</div>
						{message && (
							<div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
								{message}
							</div>
						)}
						<div style={{ fontSize: 11, color: s.dateColor, marginTop: 4 }}>{metaLabel}</div>
					</button>
				</div>

				{onDelete && (
					<button
						type="button"
						onClick={onDelete}
						aria-label="Elimina notifica"
						title="Elimina notifica"
						className="btn btn-outline"
						style={{
							fontSize: 11,
							padding: '3px 7px',
							flexShrink: 0,
							marginLeft: 8,
							color: 'var(--color-danger)',
							borderColor: 'rgba(239,68,68,0.3)',
						}}
					>
						<Trash2 style={{ width: 13, height: 13 }} />
					</button>
				)}
			</div>
		</div>
	);
};
AdminNotificationRow.displayName = 'AdminNotificationRow';

export interface NotificationRowListProps {
	/** Righe {@link AdminNotificationRow} da elencare verticalmente. */
	children: React.ReactNode;
}

/** Contenitore verticale per le righe {@link AdminNotificationRow}. */
const NotificationRowList = ({ children }: NotificationRowListProps) => (
	<div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>{children}</div>
);
NotificationRowList.displayName = 'NotificationRowList';

export interface NotificationEmptyProps {
	/** Messaggio da mostrare quando non ci sono notifiche. */
	children: React.ReactNode;
}

/** Messaggio vuoto attenuato inline dentro una card notifiche. */
const NotificationEmpty = ({ children }: NotificationEmptyProps) => (
	<div style={{ fontSize: 13, color: 'var(--color-text-muted)', padding: '8px 0' }}>{children}</div>
);
NotificationEmpty.displayName = 'NotificationEmpty';

export interface AttachmentViewerDialogProps {
	/** URL dell'allegato da visualizzare/scaricare. */
	url: string;
	/** Nome del file, mostrato nel titolo e usato per il download. */
	name: string;
	/** Chiamata alla chiusura del dialog. */
	onClose: () => void;
	/** Renderizza l'anteprima immagine (l'app inietta il proprio componente Image); se assente usa un `<img>` generico. */
	renderImage?: (url: string, name: string) => React.ReactNode;
}

/** Anteprima a tutto formato di un allegato: iframe per i PDF o immagine, con azione di download. */
const AttachmentViewerDialog = ({
	url,
	name,
	onClose,
	renderImage,
}: AttachmentViewerDialogProps) => (
	<Dialog open onOpenChange={(open) => !open && onClose()}>
		<DialogContent
			hideCloseButton
			tone="card"
			className="flex max-h-[90vh] w-[90vw] max-w-4xl flex-col gap-0 p-0"
		>
			<DialogTitle visuallyHidden>{name}</DialogTitle>
			<div className="flex items-center justify-between border-b border-border px-4 py-3">
				<div className="flex items-center gap-2">
					<Paperclip className="h-4 w-4 text-accent" />
					<span className="text-xs font-semibold">{name}</span>
				</div>
				<div className="flex items-center gap-2">
					<a
						href={url}
						download
						className="inline-flex items-center gap-1.5 rounded-[7px] border border-border bg-card px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent"
					>
						Scarica
					</a>
					<button
						type="button"
						onClick={onClose}
						aria-label="Chiudi"
						className="rounded p-1.5 text-text-muted transition-colors hover:bg-accent/10 hover:text-accent"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
			</div>
			<div className="flex-1 overflow-hidden">
				{/\.pdf(\?|$)/i.test(url) ? (
					<iframe src={url} className="h-[75vh] w-full border-0" title={name} />
				) : (
					<div className="flex h-[75vh] items-center justify-center overflow-auto bg-background p-4">
						{renderImage ? (
							renderImage(url, name)
						) : (
							// biome-ignore lint/performance/noImgElement: generic fallback preview
							<img src={url} alt={name} className="max-h-full max-w-full rounded object-contain" />
						)}
					</div>
				)}
			</div>
		</DialogContent>
	</Dialog>
);
AttachmentViewerDialog.displayName = 'AttachmentViewerDialog';

export { AdminNotificationRow, AttachmentViewerDialog, NotificationEmpty, NotificationRowList };
